import { useEffect, useMemo, type CSSProperties, type ReactNode } from 'react';

import type { ColorRamp, YearTheme } from '@/data/types';

interface YearThemeProviderProps {
  theme: YearTheme;
  children: ReactNode;
}

// Map a ramp to its CSS custom properties (e.g. --color-primary-500). Tailwind v4
// utilities (bg-primary-500, text-accent-600, …) resolve these variables via var(),
// so overriding them on a wrapper element recolors the whole subtree at runtime —
// no rebuild, and it composes with dark mode (the dark: variants resolve the same vars).
function rampToVars(name: string, ramp: ColorRamp): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [shade, value] of Object.entries(ramp)) {
    vars[`--color-${name}-${shade}`] = value;
  }
  return vars;
}

function themeToVars(theme: YearTheme): CSSProperties {
  return {
    ...rampToVars('primary', theme.primary),
    ...rampToVars('accent', theme.accent),
    ...rampToVars('secondary', theme.secondary),
    ...rampToVars('success', theme.success),
  } as CSSProperties;
}

/**
 * Applies a year's palette by overriding the Tailwind color tokens as CSS variables
 * on a wrapping element, and syncs the mobile browser chrome color (meta theme-color)
 * to the year's primary shade.
 */
export function YearThemeProvider({ theme, children }: YearThemeProviderProps) {
  const styleVars = useMemo(() => themeToVars(theme), [theme]);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', theme.primary['600']);
    }
  }, [theme]);

  return (
    <div style={styleVars} className="contents">
      {children}
    </div>
  );
}
